import React, { useContext } from 'react';

import { StyleSheet, TouchableHighlight, View } from 'react-native';
import DyteClient from '@dytesdk/web-core';
import { useDyteSelector } from '@dytesdk/react-native-core';
import DyteText from './DyteText';
import { SvgXml } from 'react-native-svg';
import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ({
  meeting,
  size,
  variant,
  iconPack = defaultIconPack,
  t,
}: {
  meeting: DyteClient;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  variant?: 'button' | 'horizontal';
  iconPack?: IconPack;
  t?: DyteI18n;
}) {
  const { storeStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const recordingState = useDyteSelector((m) => m.recording.recordingState);
  let buttonSize = 25;
  let buttonHorizontal = variant === 'horizontal' ? true : false;
  switch (size) {
    case 'md':
      buttonSize = 50;
      break;
    case 'lg':
      buttonSize = 100;
      break;
    case 'xl':
      buttonSize = 150;
      break;
  }
  switch (variant) {
    case 'horizontal':
      buttonHorizontal = true;
      break;
  }
  const styles = StyleSheet.create({
    container: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    panel: {
      flexDirection: 'row',
      width: '100%',
      paddingVertical: 12,
      paddingLeft: 48,
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
    horizontalButton: {
      height: buttonSize * 2,
      width: '100%',
      backgroundColor: colors.background[1000],
    },
    button: {
      height: buttonSize * 2,
      width: '100%',
      backgroundColor: colors.background[1000],
    },
    text: {
      paddingHorizontal: 18,
      color: colors.text,
    },
  });
  if (
    !meeting.self.permissions.canRecord ||
    meeting.stage.status !== 'ON_STAGE'
  )
    return null;
  return (
    <View style={styles.container}>
      {buttonHorizontal && (
        <TouchableHighlight
          underlayColor={colors.background[800]}
          onPress={() => {
            if (recordingState === 'IDLE') meeting.recording.start();
            else if (recordingState === 'RECORDING') meeting.recording.stop();
          }}
          disabled={
            recordingState === 'STARTING' || recordingState === 'STOPPING'
          }
          style={styles.horizontalButton}
        >
          <View style={styles.panel}>
            <SvgXml
              xml={iconPack.recording.replace('currentColor', colors.danger)}
              width={25}
              height={25}
            />
            {recordingState === 'IDLE' && (
              <DyteText style={styles.text}>{t('recording.start')}</DyteText>
            )}
            {recordingState === 'RECORDING' && (
              <DyteText style={styles.text}>{t('recording.stop')}</DyteText>
            )}
            {recordingState === 'STARTING' && (
              <DyteText style={styles.text}>{t('recording.starting')}</DyteText>
            )}
            {recordingState === 'STOPPING' && (
              <DyteText style={styles.text}>{t('recording.stopping')}</DyteText>
            )}
          </View>
        </TouchableHighlight>
      )}
      {!buttonHorizontal && (
        <TouchableHighlight
          underlayColor={colors.background[800]}
          onPress={() => {
            if (recordingState === 'RECORDING') meeting.recording.start();
            else if (recordingState === 'IDLE') meeting.recording.stop();
          }}
          style={styles.button}
        >
          <View style={styles.panel}>
            <SvgXml
              xml={iconPack.recording}
              width={25}
              height={25}
              color={colors.danger}
            />
          </View>
        </TouchableHighlight>
      )}
    </View>
  );
}
